import {
	FILTER_LOCATION_KEY,
	FILTER_WORK_CENTERS_KEY,
} from "../../../assets/constants";
import { WORKER_TYPES } from "../../../components/filters/constants";
import {
	AppliedFilter,
	DataRelation,
	FilterData,
} from "../../../components/filters/filter-context";
import { RegularShiftRequest } from "../store/worker-requests-types";

// Chip names shown in the AppliedFilters bar. These double as the `type` used
// when matching a row/leave/count against the applied filters.
export const WR_FILTER_LOCATION = "Location";
export const WR_FILTER_WORK_CENTER = "Work Center";
export const WR_FILTER_SHIFT_PATTERN = "Shift Pattern";
export const WR_FILTER_SHIFT_WORK_CENTER = "Shift Work Center";
export const WR_FILTER_STATUS = "Status";

// Call Logs "Primary Job" filter — options come from GetFiltersForUser (the
// full job list) and rows are matched by the worker's primary job id. Reuses
// the shared FILTER_JOB_KEY so the filterRelationships cascade resolves.
export const WR_FILTER_PRIMARY_JOB = "Primary Job";

// Call Logs "Call outcome" filter — whether a request was generated from the
// call. Rendered as a chip in the Call Logs AppliedFilters bar, defaulting to
// "No request created" so supervisors first see calls needing attention.
export const WR_FILTER_CALL_LINK = "Call outcome";
export const CALL_LINK_FILTER_KEY = "callRequestLink";
export const CALL_LINK_UNLINKED = "unlinked";
export const CALL_LINK_LINKED = "linked";
export const CALL_LINK_UNLINKED_OPTION = {
	id: CALL_LINK_UNLINKED,
	name: "No request created",
	labelKey: "worker-requests_no-request-created",
};
export const CALL_LINK_LINKED_OPTION = {
	id: CALL_LINK_LINKED,
	name: "Request created",
	labelKey: "worker-requests_request-created",
};
export const CALL_LINK_OPTIONS = [
	CALL_LINK_UNLINKED_OPTION,
	CALL_LINK_LINKED_OPTION,
];

// Redux/filter keys. Location, work center and shift pattern reuse the shared
// keys so the location→work-center cascade from GetFiltersForUser applies.
export const WR_STATUS_FILTER_KEY = "requestStatuses";
export const FLEX_STATUS_FILTER_KEY = "flexRequestStatuses";

// Status code for the "Pending" request status, preselected on the Requests tab
// so supervisors first see requests awaiting action.
export const WR_STATUS_PENDING = "p";

const getSelected = (
	appliedFilters: AppliedFilter[],
	type: string
): FilterData[] | undefined => {
	const applied = appliedFilters.find((af) => af.type === type);
	return applied && applied.data.length > 0 ? applied.data : undefined;
};

const toNumericId = (id: string | number): number =>
	typeof id === "string" ? parseInt(id, 10) : id;

// When a Location filter is applied, resolve it to the set of work-center IDs
// belonging to the selected locations via the location→work-center mapping
// returned by GetFiltersForUser. Returns null when no location is applied, so
// callers can skip location matching entirely.
export const getAllowedWorkcenterIds = (
	appliedFilters: AppliedFilter[],
	dataRelations: DataRelation[]
): Set<number> | null => {
	const selectedLocations = getSelected(appliedFilters, WR_FILTER_LOCATION);
	if (!selectedLocations) return null;

	const relation = (dataRelations || []).find(
		(r) => r.from === FILTER_LOCATION_KEY && r.to === FILTER_WORK_CENTERS_KEY
	);
	if (!relation || !Array.isArray(relation.mappings)) return new Set();

	const locationIds = new Set(
		selectedLocations.map((loc) => toNumericId(loc.id))
	);
	const allowed = new Set<number>();
	relation.mappings.forEach((mapping) => {
		if (locationIds.has(mapping.fromId)) {
			(mapping.toIds || []).forEach((id) => allowed.add(id));
		}
	});
	return allowed;
};

// Matches a table row (RegularShiftRequest) against the applied filters. Work
// center is matched by id (the nested worker carries workCenterId), status by
// code (FilterData.id holds the status code), location by resolving it to the
// set of allowed work-center ids, and shift pattern by name (the only field the
// payload provides for the pattern).
export const matchesTableFilters = (
	row: RegularShiftRequest,
	appliedFilters: AppliedFilter[],
	dataRelations: DataRelation[]
): boolean => {
	const rowWorkCenterId = row.worker?.workCenterId ?? null;

	const workCenters = getSelected(appliedFilters, WR_FILTER_WORK_CENTER);
	if (workCenters) {
		const ids = new Set(workCenters.map((item) => toNumericId(item.id)));
		if (rowWorkCenterId == null || !ids.has(rowWorkCenterId)) return false;
	}

	const shiftPatterns = getSelected(appliedFilters, WR_FILTER_SHIFT_PATTERN);
	if (shiftPatterns) {
		const names = new Set(shiftPatterns.map((item) => item.name));
		if (!row.ftShift?.patternName || !names.has(row.ftShift.patternName)) {
			return false;
		}
	}

	const statuses = getSelected(appliedFilters, WR_FILTER_STATUS);
	if (statuses) {
		const codes = new Set(statuses.map((item) => item.id));
		if (!row.status?.code || !codes.has(row.status.code)) return false;
	}

	const allowedWcIds = getAllowedWorkcenterIds(appliedFilters, dataRelations);
	if (allowedWcIds) {
		if (rowWorkCenterId == null || !allowedWcIds.has(rowWorkCenterId)) {
			return false;
		}
	}

	return true;
};

// Sub type ids in the hierarchical Worker Type filter are offset so they
// don't collide with the parent worker type ids (workers page convention).
export const WORKER_SUB_TYPE_ID_OFFSET = 1000;

interface FlexSwapRow {
	shiftLocationId?: number | null;
	approvalStatus?: string | null;
	shiftWorkCenterId?: number | null;
	fromWorker?: {
		workerTypeText?: string | null;
		workerSubTypeId?: number | null;
	} | null;
}

// Matches a flex shift swap row against the applied filters. Location and work
// center are matched by id (rows carry shiftLocationId / shiftWorkCenterId),
// worker type by name for parent types (rows carry workerTypeText but no type
// id) and by id for sub types, and status by code (FilterData.id holds the
// status code, compared case-insensitively against the row's approvalStatus).
export const matchesFlexTableFilters = (
	row: FlexSwapRow,
	appliedFilters: AppliedFilter[]
): boolean => {
	const locations = getSelected(appliedFilters, WR_FILTER_LOCATION);
	if (locations) {
		const ids = new Set(locations.map((item) => toNumericId(item.id)));
		if (row.shiftLocationId == null || !ids.has(row.shiftLocationId)) {
			return false;
		}
	}

	const workCenters = getSelected(
		appliedFilters,
		WR_FILTER_SHIFT_WORK_CENTER
	);
	if (workCenters) {
		const ids = new Set(workCenters.map((item) => Number(item.id)));
		if (row.shiftWorkCenterId == null || !ids.has(row.shiftWorkCenterId)) {
			return false;
		}
	}

	const workerTypes = getSelected(appliedFilters, WORKER_TYPES);
	if (workerTypes) {
		const typeText = row.fromWorker?.workerTypeText;
		const subTypeId = row.fromWorker?.workerSubTypeId;

		const isParentSelected = workerTypes.some(
			(item) => item.parentId == null && item.name === typeText
		);
		const selectedSubTypeIds = new Set(
			workerTypes
				.filter((item) => item.parentId != null)
				.map((item) => Number(item.id) - WORKER_SUB_TYPE_ID_OFFSET)
		);
		const isSubTypeSelected =
			subTypeId != null && selectedSubTypeIds.has(subTypeId);

		// Attendance-report rule: the row's worker type must be selected; when
		// any sub types are selected too, the row must match one of them
		// (expanding a parent in the popover auto-selects it, so a plain OR
		// would make sub type selections have no effect).
		if (!isParentSelected) return false;
		if (selectedSubTypeIds.size > 0 && !isSubTypeSelected) return false;
	}

	const statuses = getSelected(appliedFilters, WR_FILTER_STATUS);
	if (statuses) {
		const codes = new Set(
			statuses.map((item) => String(item.id).toLowerCase())
		);
		if (
			!row.approvalStatus ||
			!codes.has(String(row.approvalStatus).toLowerCase())
		) {
			return false;
		}
	}

	return true;
};

// Matches a call log entry against the applied filters. A call record carries
// the worker's work center id, primary job id (no location) and its linked
// request IDs, so Work Center, Primary Job and Call outcome apply here. Work
// Center and Primary Job are both matched by id (the filter options come from
// GetFiltersForUser, whose FilterData.id holds the work center / job id).
export const matchesCallLogFilters = (
	row: {
		worker?: {
			workCenterId?: number | null;
			primaryJobId?: number | null;
		};
		linkedRequestIds?: number[];
	},
	appliedFilters: AppliedFilter[]
): boolean => {
	// Call outcome filter: match on whether a request was generated from the
	// call. Selecting both options (or none) does not restrict.
	const linkStatus = getSelected(appliedFilters, WR_FILTER_CALL_LINK);
	if (linkStatus) {
		const ids = new Set(linkStatus.map((item) => String(item.id)));
		const hasRequest = (row.linkedRequestIds?.length ?? 0) > 0;
		const wantLinked = ids.has(CALL_LINK_LINKED);
		const wantUnlinked = ids.has(CALL_LINK_UNLINKED);
		if (!(wantLinked && wantUnlinked)) {
			if (wantUnlinked && hasRequest) return false;
			if (wantLinked && !hasRequest) return false;
		}
	}

	const workCenters = getSelected(appliedFilters, WR_FILTER_WORK_CENTER);
	if (workCenters) {
		const ids = new Set(workCenters.map((item) => toNumericId(item.id)));
		if (row.worker?.workCenterId == null || !ids.has(row.worker.workCenterId)) {
			return false;
		}
	}

	const primaryJobs = getSelected(appliedFilters, WR_FILTER_PRIMARY_JOB);
	if (primaryJobs) {
		const ids = new Set(primaryJobs.map((item) => toNumericId(item.id)));
		if (row.worker?.primaryJobId == null || !ids.has(row.worker.primaryJobId)) {
			return false;
		}
	}

	return true;
};
